import React, { lazy, Suspense, useState, useLayoutEffect } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate, useLocation } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import SplashScreen from "components/ui/SplashScreen";
import OAuthHandler from './components/OAuthHandler';
import Footer from './components/ui/Footer';
import i18n from './i18n/config';

// Critical routes - loaded immediately
import HomeRedirect from './pages/home-redirect';

// Lazy-loaded routes - loaded on demand
// A route whose namespaces are fetched beside its page chunk, so both load under one splash.
const lazyWithNamespaces = (load, namespaces) =>
  lazy(() => Promise.all([load(), i18n.loadNamespaces(namespaces)]).then(([page]) => page));

// Onboarding is the first screen a new visitor sees, so its namespace is fetched
// beside the page chunk rather than after it renders (see apps/web/CLAUDE.md, i18n):
// otherwise the page suspends on it before its own data requests can start.
const OnboardingPage = lazyWithNamespaces(() => import('./pages/onboarding'), 'onboarding');
// A shared event link lands here first, so its namespaces come beside the chunk too —
// `feed` included, which the page asks for on first render for its related-event cards.
const EventDetails = lazyWithNamespaces(() => import('./pages/event-details'), ['event', 'feed']);
const EventRecommendations = lazy(() => import('./pages/event-recommendations'));
// Search renders `feed`'s filters on its first render beside its own `search` copy, so both
// namespaces are fetched with the page chunk rather than after it renders.
const SearchResults = lazyWithNamespaces(() => import('./pages/search-results'), ['search', 'feed']);
const LikesPage = lazy(() => import('./pages/likes'));
const DislikesPage = lazy(() => import('./pages/dislikes'));
const ProfilePage = lazy(() => import('./pages/profile'));
const LoginPage = lazy(() => import('./pages/login'));
const TermsPage = lazy(() => import('./pages/terms'));
const PrivacyPage = lazy(() => import('./pages/privacy'));
const CookiesPage = lazy(() => import('./pages/cookies'));
const LegalNoticePage = lazy(() => import('./pages/legal-notice'));
const BotPage = lazy(() => import('./pages/bot'));
// FAQ, contact and reset-password (a link in an email, so a cold first visit) share the
// `misc` namespace, fetched beside each page chunk — the 404 too, which is lazy for that
// reason: bundled eagerly, it would wait on the namespace alone after the entry had run.
const ResetPasswordPage = lazyWithNamespaces(() => import('./pages/reset-password'), 'misc');
const FAQPage = lazyWithNamespaces(() => import('./pages/faq'), 'misc');
const ContactPage = lazyWithNamespaces(() => import('./pages/contact'), 'misc');
const NotFound = lazyWithNamespaces(() => import('./pages/NotFound'), 'misc');

const LoadingFallback = () => <SplashScreen />;

// Mounts only when Suspense resolves. useLayoutEffect fires before paint so the
// footer appears in the same frame as the page — no layout shift.
// appReady is a one-way latch: once true it never resets, so the footer stays
// visible during subsequent lazy-route splashes (SplashScreen z-[200] covers it).
export const AppReadyMarker = ({ onReady }) => {
  useLayoutEffect(() => { onReady(true); }, [onReady]);
  return null;
};

// Settings was merged into Profile. A bare `<Navigate to="/profile" />` would
// drop any query string or hash an incoming link carried (e.g. /settings#password) —
// this preserves both.
export const SettingsRedirect = () => {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: '/profile', search, hash }} replace />;
};

// Lives outside <Suspense> so it persists through lazy-load transitions,
// but is withheld until Suspense has resolved at least once (appReady flag).
// '/' is excluded because HomeRedirect mounts without suspending, setting
// appReady too early before the actual destination route loads.
export const ConditionalFooter = () => {
  const { pathname } = useLocation();
  if (pathname === '/onboarding' || pathname === '/') return null;
  return <Footer />;
};

const Routes = () => {
  const [appReady, setAppReady] = useState(false);

  return (
    <BrowserRouter>
      <ErrorBoundary>
        <OAuthHandler>
          <ScrollToTop />
          <Suspense fallback={<LoadingFallback />}>
            <RouterRoutes>
              <Route path="/onboarding" element={<OnboardingPage />} />
              <Route path="/" element={<HomeRedirect />} />
              <Route path="/events/:city/:domain/:slug" element={<EventDetails />} />
              <Route path="/event-details" element={<EventDetails />} />
              <Route path="/event-recommendations" element={<EventRecommendations />} />
              <Route path="/search-results" element={<SearchResults />} />
              <Route path="/likes" element={<LikesPage />} />
              <Route path="/dislikes" element={<DislikesPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/settings" element={<SettingsRedirect />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/cookies" element={<CookiesPage />} />
              <Route path="/legal-notice" element={<LegalNoticePage />} />
              <Route path="/bot" element={<BotPage />} />
              <Route path="*" element={<NotFound />} />
            </RouterRoutes>
            <AppReadyMarker onReady={setAppReady} />
          </Suspense>
          {appReady && <ConditionalFooter />}
        </OAuthHandler>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
